// STRATUL 3D AL INSTALAȚIILOR: un grup gata construit, pus în orice machetă
// care îl primește (azi `plan/Randare3D`, nivelul în 3D) și scos din ea fără
// să-i atingă restul scenei — ca stratul orașului (`viewer/oras/strat3d.ts`).
//
// Țevile sunt câte un `InstancedMesh` de cilindru unitate PE SISTEM, ca
// armătura (`structure/armatura3d.ts`): un singur desen pe sistem, oricâte
// tronsoane. Canalele dreptunghiulare și echipamentele sunt cutii instanțiate,
// ca pereții din `plan/Randare3D`. Geometria stă în cadrul documentului (+y
// sud); macheta gazdă o pune în `lume`, care face întoarcerea (ui/CLAUDE.md).

import * as THREE from "three";
import { CULOARE, type Echipament, type ModelInstalatii, type Segment, type Sistem } from "./model";

/** Raza minimă desenată: o țeavă DN 16 are 1 cm, adică sub un pixel la scara
 *  unui nivel. Legenda o spune „îngroșat pentru vizibilitate”. */
export const RAZA_MIN_M = 0.012;
/** În șapă, țeava se desenează mai deschis: e îngropată, nu aparentă. */
const DESCHIS_SAPA = 0.45;
/** Cât stă desenul țevii din șapă deasupra pardoselii machetei, ca să nu
 *  pâlpâie în ea. */
const PESTE_PARDOSEALA_M = 0.005;
const CULOARE_ALES = 0xffc107;

export interface OptiuniStrat {
  /** Doar nivelurile date (și coloanele care trec prin ele), cu tot ce e
   *  între `zJos` și `zSus`; fără ele, toată clădirea. */
  niveluri?: ReadonlySet<string>;
  zJos?: number;
  zSus?: number;
  /** Cota care ajunge la z = 0 în machetă (pardoseala nivelului desenat). */
  zBaza?: number;
}

export interface StratInstalatii {
  grup: THREE.Group;
  /** Aprinde și stinge sistemele fără reconstrucție. */
  arata(vizibile: ReadonlySet<Sistem>): void;
  /** Colorează elementele alese (segmente, echipamente). */
  evidentiaza(ids: ReadonlySet<string>): void;
  /** Id-ul elementului de sub rază, sau `null`. */
  alege(lovit: THREE.Intersection): string | null;
  /** Id-ul țevii vizibile cea mai apropiată de cameră care trece la cel mult
   *  `toleranta_m` de rază (raza în cadrul documentului): o țeavă de 2 cm are
   *  sub un pixel la scara nivelului, iar o rază exactă ar trece pe lângă ea. */
  alegeAproape(raza: THREE.Ray, toleranta_m: number): string | null;
  elibereaza(): void;
}

interface Piesa { id: string; a: THREE.Vector3; b: THREE.Vector3; r: number; culoare: THREE.Color;
                  l?: number; h?: number }
interface Cutie { id: string; c: THREE.Vector3; dim: [number, number, number]; deg: number;
                  culoare: THREE.Color }

/** Bucățile segmentului din fereastra de cote, coborâte la `zBaza`: o
 *  coloană care trece prin nivel se arată doar cât stă în el. */
function decupeaza(s: Segment, o: OptiuniStrat): THREE.Vector3[][] {
  const z0 = o.zJos ?? -Infinity, z1 = o.zSus ?? Infinity, baza = o.zBaza ?? 0;
  const out: THREE.Vector3[][] = [];
  for (let i = 1; i < s.puncte.length; i++) {
    const a = s.puncte[i - 1], b = s.puncte[i];
    const dz = b[2] - a[2];
    let k0 = 0, k1 = 1;
    if (Math.abs(dz) < 1e-9) {
      if (a[2] < z0 || a[2] > z1) continue;
    } else {
      const ka = (z0 - a[2]) / dz, kb = (z1 - a[2]) / dz;
      k0 = Math.max(0, Math.min(ka, kb));
      k1 = Math.min(1, Math.max(ka, kb));
      if (k1 <= k0) continue;
    }
    const v = (k: number) => new THREE.Vector3(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k,
                                               a[2] + dz * k - baza);
    out.push([v(k0), v(k1)]);
  }
  return out;
}

export function construiesteStrat(m: ModelInstalatii, o: OptiuniStrat = {}): StratInstalatii {
  const grup = new THREE.Group();
  grup.name = "instalatii";
  const deArs: { dispose(): void }[] = [];
  const peSistem = new Map<Sistem, THREE.Group>();
  const plase: { mesh: THREE.InstancedMesh; ids: string[]; culori: THREE.Color[] }[] = [];

  // coloanele au nivelul de jos: se văd pe orice nivel prin care trec, tăiate
  const vertical = (s: Segment) => s.puncte.length === 2
    && Math.hypot(s.puncte[0][0] - s.puncte[1][0], s.puncte[0][1] - s.puncte[1][1]) < 1e-6;
  const vede = (s: Segment) => !o.niveluri || (!!s.storey && o.niveluri.has(s.storey)) || vertical(s);

  const tevi = new Map<Sistem, Piesa[]>();
  const cutii = new Map<Sistem, Cutie[]>();
  /** Ghenele propuse, translucide: înăuntru stă coloana, care trebuie să se vadă. */
  const ghene: Cutie[] = [];
  for (const s of m.segmente) {
    if (!vede(s)) continue;
    const culoare = new THREE.Color(CULOARE[s.sistem]);
    if (s.mediu === "sapa") culoare.lerp(new THREE.Color(0xffffff), DESCHIS_SAPA);
    const r = Math.max(s.d_m / 2, RAZA_MIN_M);
    for (const [a, b] of decupeaza(s, o)) {
      // pardoseala machetei e opacă: ce trece prin șapă s-ar ascunde sub ea,
      // deci se desenează pe fața ei, mai deschis (ca punctatul din plan)
      if (s.mediu === "sapa") for (const p of [a, b]) p.z = Math.max(p.z, r + PESTE_PARDOSEALA_M);
      if (a.distanceTo(b) < 1e-4) continue;
      const lista = tevi.get(s.sistem) ?? [];
      lista.push({ id: s.id, a, b, r, culoare, l: s.sectiune?.l, h: s.sectiune?.h });
      tevi.set(s.sistem, lista);
    }
  }
  const baza = o.zBaza ?? 0;
  for (const e of m.echipamente) {
    if (o.niveluri && !(e.storey && o.niveluri.has(e.storey))) continue;
    const cu: Cutie = { id: e.id, c: new THREE.Vector3(e.centru[0], e.centru[1], e.centru[2] + e.dim[2] / 2 - baza),
                        dim: e.dim, deg: e.deg ?? 0, culoare: culoareEchipament(e) };
    if (e.fel === "ghena_propusa") { ghene.push(cu); continue; }
    cutii.set(e.sistem, [...(cutii.get(e.sistem) ?? []), cu]);
  }

  const cilindru = new THREE.CylinderGeometry(1, 1, 1, 10, 1, false);
  const cub = new THREE.BoxGeometry(1, 1, 1);
  deArs.push(cilindru, cub);
  const material = () => {
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.1 });
    deArs.push(mat);
    return mat;
  };
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
  const sus = new THREE.Vector3(0, 1, 0), dir = new THREE.Vector3(), mij = new THREE.Vector3();

  const sisteme = new Set<Sistem>([...tevi.keys(), ...cutii.keys()]);
  for (const sistem of sisteme) {
    const g = new THREE.Group();
    g.name = `instalatii-${sistem}`;
    g.userData.sistem = sistem;
    grup.add(g);
    peSistem.set(sistem, g);
    const rotunde = (tevi.get(sistem) ?? []).filter((p) => p.l == null);
    const drepte = (tevi.get(sistem) ?? []).filter((p) => p.l != null);
    if (rotunde.length) {
      const mesh = new THREE.InstancedMesh(cilindru, material(), rotunde.length);
      rotunde.forEach((p, i) => {
        dir.subVectors(p.b, p.a);
        const L = dir.length();
        mij.addVectors(p.a, p.b).multiplyScalar(0.5);
        q.setFromUnitVectors(sus, dir.normalize());
        mesh.setMatrixAt(i, m4.compose(mij, q, sc.set(p.r, L, p.r)));
        mesh.setColorAt(i, p.culoare);
      });
      fixeaza(mesh);
      g.add(mesh);
      plase.push({ mesh, ids: rotunde.map((p) => p.id), culori: rotunde.map((p) => p.culoare) });
    }
    const box = [
      ...drepte.map((p) => {
        dir.subVectors(p.b, p.a);
        const L = dir.length();
        return { id: p.id, c: p.a.clone().add(p.b).multiplyScalar(0.5), culoare: p.culoare,
                 q: new THREE.Quaternion().setFromUnitVectors(sus, dir.clone().normalize()),
                 s: new THREE.Vector3(p.l!, L, p.h!) };
      }),
      ...(cutii.get(sistem) ?? []).map((cu) => ({
        id: cu.id, c: cu.c, culoare: cu.culoare,
        q: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (cu.deg * Math.PI) / 180),
        s: new THREE.Vector3(cu.dim[0], cu.dim[1], cu.dim[2]),
      })),
    ];
    if (box.length) {
      const mesh = new THREE.InstancedMesh(cub, material(), box.length);
      box.forEach((b, i) => {
        mesh.setMatrixAt(i, m4.compose(b.c, b.q, b.s));
        mesh.setColorAt(i, b.culoare);
      });
      fixeaza(mesh);
      g.add(mesh);
      plase.push({ mesh, ids: box.map((b) => b.id), culori: box.map((b) => b.culoare) });
    }
  }

  if (ghene.length) {
    const mat = new THREE.MeshStandardMaterial({ color: 0xc9a27a, transparent: true, opacity: 0.25,
                                                 depthWrite: false, roughness: 0.9 });
    deArs.push(mat);
    const mesh = new THREE.InstancedMesh(cub, mat, ghene.length);
    ghene.forEach((cu, i) => mesh.setMatrixAt(i, m4.compose(
      cu.c, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (cu.deg * Math.PI) / 180),
      new THREE.Vector3(...cu.dim))));
    fixeaza(mesh);
    mesh.renderOrder = 2;   // după opace, ca transparența să nu ascundă țevile
    let g = peSistem.get("canalizare");
    if (!g) {
      g = new THREE.Group();
      g.name = "instalatii-canalizare";
      grup.add(g);
      peSistem.set("canalizare", g);
    }
    g.add(mesh);
    plase.push({ mesh, ids: ghene.map((cu) => cu.id), culori: ghene.map((cu) => cu.culoare) });
  }

  const ales = new THREE.Color(CULOARE_ALES);
  return {
    grup,
    arata: (vizibile) => { for (const [s, g] of peSistem) g.visible = vizibile.has(s); },
    evidentiaza: (ids) => {
      for (const p of plase) {
        p.ids.forEach((id, i) => p.mesh.setColorAt(i, ids.has(id) ? ales : p.culori[i]));
        if (p.mesh.instanceColor) p.mesh.instanceColor.needsUpdate = true;
      }
    },
    alege: (lovit) => {
      const p = plase.find((x) => x.mesh === lovit.object);
      if (!p || lovit.instanceId == null) return null;
      // Raycaster-ul nu ține seama de `visible`: un sistem stins nu se alege
      let o: THREE.Object3D | null = p.mesh;
      while (o) { if (!o.visible) return null; o = o.parent; }
      return p.ids[lovit.instanceId] ?? null;
    },
    alegeAproape: (raza, toleranta_m) => {
      let id: string | null = null, celMaiAproape = Infinity;
      const peRaza = new THREE.Vector3();
      for (const [sistem, lista] of tevi) {
        if (!grup.visible || !peSistem.get(sistem)?.visible) continue;
        for (const p of lista) {
          const tol = p.r + toleranta_m;
          if (raza.distanceSqToSegment(p.a, p.b, peRaza) > tol * tol) continue;
          const t = peRaza.distanceTo(raza.origin);
          if (t < celMaiAproape) { celMaiAproape = t; id = p.id; }
        }
      }
      return id;
    },
    elibereaza: () => {
      grup.removeFromParent();
      for (const p of plase) p.mesh.dispose();
      deArs.forEach((x) => x.dispose());
    },
  };
}

/** După `setMatrixAt` sfera de încadrare e a geometriei unitate: recalculată,
 *  altfel instanțele de departe ar dispărea la marginea ecranului. */
function fixeaza(mesh: THREE.InstancedMesh): void {
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.castShadow = false;
  mesh.receiveShadow = false;
}

function culoareEchipament(e: Echipament): THREE.Color {
  if (e.fel === "ghena_propusa") return new THREE.Color(0xc9a27a);
  return new THREE.Color(CULOARE[e.sistem]).lerp(new THREE.Color(0xffffff), 0.2);
}
